"use client"

import * as React from "react"

import type { UserRole } from "@/types/auth"
import { useAuth } from "@/contexts/AuthContext"
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarRail,
  SidebarSeparator,
} from "@/components/ui/sidebar"
import { TeamSwitcher } from "./TeamSwitcher"
import { NavMain } from "./NavMain"
import {
  allMainNavItems,
  allEdgesNavItems,
  allAdminItems,
  helpNavItems,
} from "@/lib/navItems"

// Role hierarchy — higher number = more permissions
const ROLE_RANK: Record<string, number> = {
  viewer: 1,
  member: 2,
  admin: 3,
  owner: 4,
}

function meetsMinRole(userRole: UserRole | null, minRole: UserRole): boolean {
  if (!userRole) return false
  return (ROLE_RANK[userRole] ?? 0) >= (ROLE_RANK[minRole] ?? 0)
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { isSystemAdmin, isSuperAdmin, userRole, activeOrganization } = useAuth()

  // For system admins (viewing as org or not), show all main items.
  // For regular org users, filter by their role in the active org.
  const mainNavItems = isSystemAdmin
    ? allMainNavItems
    : allMainNavItems.filter(item => !item.minRole || meetsMinRole(userRole, item.minRole))

  // Admin section visibility:
  //   - System admins (super_admin / support) see admin items, filtered by
  //     superAdminOnly.
  //   - Org admins of orgs with a `requireOrgFeature` flag enabled see ONLY
  //     the items that name that feature (e.g. /admin/qa-automation for
  //     qaAutomation tenants). They don't see the rest of the admin section.
  //   - All others see no admin section.
  const orgFeatures = activeOrganization?.settings?.features || {}
  const orgIsAdmin = meetsMinRole(userRole, "admin")
  const adminNavItems = allAdminItems.filter(item => {
    if (item.requireOrgFeature) {
      // Feature-gated item: super-admin always sees it; org admin sees it only
      // when the active org has the flag explicitly enabled.
      if (isSuperAdmin) return true
      if (orgIsAdmin && orgFeatures[item.requireOrgFeature] === true) return true
      return false
    }
    if (!isSystemAdmin) return false
    if (item.superAdminOnly && !isSuperAdmin) return false
    return true
  })

  // Edges section: owner/admin-only items filtered for non-admins.
  // /edges itself is open to every plan (#270) — it is how a customer sees the
  // SimCheck network locations their tests run on. Only PROVISIONING (bringing
  // your own hardware) carries the Business/Enterprise gate.
  const isEdgesAdmin = isSuperAdmin || meetsMinRole(userRole, "admin")
  const orgPlan = activeOrganization?.subscription?.plan ?? ''
  const hasOwnEdgeAccess = isSuperAdmin || ['business', 'enterprise'].includes(orgPlan)
  const edgesNavItems = allEdgesNavItems.filter(item => {
    if (item.url === '/edges/provisioning' && !hasOwnEdgeAccess) return false
    if ((item as any).adminAction && !isEdgesAdmin) return false
    return true
  })

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <TeamSwitcher />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={mainNavItems} />
        <SidebarSeparator />
        <NavMain items={edgesNavItems} label="Infrastructure" />
        {adminNavItems.length > 0 && (
          <>
            <SidebarSeparator />
            <NavMain items={adminNavItems} label="Admin" />
          </>
        )}
        <SidebarSeparator />
        <NavMain items={helpNavItems} label="Help" />
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  )
}
